import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { SITE_CONFIG } from '@/data/siteData';
import { SEO_INTERNAL_LINKS } from '@/data/seoPages';
import { SEO_FOOTER_ZONES, SEO_FOOTER_SERVICES } from '@/data/newSeoPages';
import { Phone, Mail, MapPin, MessageCircle } from 'lucide-react';

const Footer = () => {
  const { lang, t } = useLanguage();

  // Split links into two columns
  const firstColumnLinks = SEO_INTERNAL_LINKS.slice(0, 5);
  const secondColumnLinks = SEO_INTERNAL_LINKS.slice(5);

  return (
    <footer className="bg-[#1a365d] text-white" data-testid="footer">
      {/* Gold Top Border */}
      <div className="h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Company Info */}
          <div>
            {/* Logo */}
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <span className="text-[#D4AF37] font-bold text-lg" style={{ fontFamily: 'Cormorant Garamond, serif' }}>VS</span>
              </div>
              <div>
                <span className="font-semibold text-white" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Riviera</span>
                <span className="text-[#D4AF37] ml-1" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Conciergerie</span>
              </div>
            </div>
            <p className="text-slate-300 mb-6 text-sm leading-relaxed">
              {t(
                "Services premium de nettoyage, gestion locative et conciergerie sur la Côte d'Azur.",
                "Premium cleaning, property management and concierge services on the French Riviera."
              )}
            </p>
            <div className="space-y-3">
              <a 
                href={SITE_CONFIG.phoneLink} 
                className="flex items-center gap-3 text-slate-300 hover:text-[#D4AF37] transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-4 h-4" />
                <span>{SITE_CONFIG.phone}</span>
              </a>
              <a 
                href={SITE_CONFIG.whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 text-slate-300 hover:text-green-400 transition-colors"
                data-testid="footer-whatsapp"
              >
                <MessageCircle className="w-4 h-4" />
                <span>WhatsApp</span>
              </a>
              <a 
                href={`mailto:${SITE_CONFIG.email}`}
                className="flex items-center gap-3 text-slate-300 hover:text-[#D4AF37] transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-4 h-4" />
                <span>{SITE_CONFIG.email}</span>
              </a>
            </div>
          </div>

          {/* SEO Links Column 1 */}
          <div>
            <h3 className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-6">
              {t("Nos services de nettoyage", "Our cleaning services")}
            </h3>
            <ul className="space-y-3">
              {firstColumnLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href}
                    className="text-slate-300 hover:text-[#D4AF37] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* SEO Links Column 2 */}
          <div>
            <h3 className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-6">
              {t("Côte d'Azur", "French Riviera")}
            </h3>
            <ul className="space-y-3">
              {secondColumnLinks.map((link, index) => (
                <li key={index}>
                  <Link 
                    to={link.href}
                    className="text-slate-300 hover:text-[#D4AF37] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
              {/* Add English page for English users */}
              {lang === 'en' && (
                <li>
                  <Link 
                    to="/airbnb-cleaning-frejus"
                    className="text-slate-300 hover:text-[#D4AF37] transition-colors text-sm"
                  >
                    Airbnb Cleaning Fréjus
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-6">
              {t('Navigation', 'Navigation')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link 
                  to="/"
                  className="text-slate-300 hover:text-[#D4AF37] transition-colors text-sm"
                >
                  {t('Accueil', 'Home')}
                </Link>
              </li>
              <li>
                <Link 
                  to="/blog"
                  className="text-slate-300 hover:text-[#D4AF37] transition-colors text-sm"
                >
                  Blog
                </Link>
              </li>
              <li>
                <a 
                  href="#contact"
                  className="text-slate-300 hover:text-[#D4AF37] transition-colors text-sm"
                >
                  {t('Contact', 'Contact')}
                </a>
              </li>
              <li>
                <a 
                  href="#devis"
                  className="text-slate-300 hover:text-[#D4AF37] transition-colors text-sm"
                >
                  {t('Devis gratuit', 'Free quote')}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* SEO Internal Links Section */}
        <div className="border-t border-white/10 mt-12 pt-8">
          <h4 className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-4 text-center">
            {t("Nos services de nettoyage sur la Côte d'Azur", "Our cleaning services on the French Riviera")}
          </h4>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {SEO_INTERNAL_LINKS.map((link, index) => (
              <Link 
                key={index}
                to={link.href}
                className="text-slate-400 hover:text-[#D4AF37] transition-colors text-xs"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>

        {/* New SEO Sections */}
        <div className="border-t border-white/10 mt-8 pt-8">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Zones d'intervention */}
            <div>
              <h4 className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-4">
                {t("Zones d'intervention", "Service Areas")}
              </h4>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {SEO_FOOTER_ZONES.map((link, index) => (
                  <Link 
                    key={index}
                    to={link.href}
                    className="text-slate-400 hover:text-[#D4AF37] transition-colors text-xs"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
            {/* Services de nettoyage */}
            <div>
              <h4 className="text-[#D4AF37] font-semibold text-sm tracking-widest uppercase mb-4">
                {t("Services de nettoyage", "Cleaning Services")}
              </h4>
              <div className="flex flex-wrap gap-x-4 gap-y-2">
                {SEO_FOOTER_SERVICES.map((link, index) => (
                  <Link 
                    key={index}
                    to={link.href}
                    className="text-slate-400 hover:text-[#D4AF37] transition-colors text-xs"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-white/10 mt-8 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">
              © {new Date().getFullYear()} {SITE_CONFIG.companyName}. {t('Tous droits réservés.', 'All rights reserved.')}
            </p>
            <div className="flex items-center gap-2 text-slate-400 text-sm">
              <MapPin className="w-4 h-4 text-[#D4AF37]" />
              <span>{t("Côte d'Azur, France", "French Riviera, France")}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
